import axios from 'axios';
import debounce from 'lodash/debounce';
import { Loader2, MapPin, Pencil, UserPlus, X } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import CustomerAvatar from '@/components/customers/CustomerAvatar';
import { Badge } from '@/components/ui/badge';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import { cn } from '@/lib/utils';
import formatPhoneForTitleBar from '@/utils/formatPhoneForUser';
import getSearchUrl from '@/utils/getSearchUrl';

export type ComposeRecipient = {
  key: string;
  _customer?: string;
  _business?: string;
  name: string;
  phoneEmail: string;
  isNew: boolean;
};

/** seed values handed to the parent-owned create-customer modal */
export type CreateContactSeed = { name: string; phone: string; email: string };

type SearchedCustomer = {
  _id: string;
  name?: string;
  photoUrl?: string | null;
  phone?: string;
  email?: string;
  _business?: string;
  latestMessageDate?: string;
};

type Option =
  | { type: 'customer'; customer: SearchedCustomer }
  | { type: 'create-customer'; query: string };

const MIN_SEARCH_CHARS = 2;

// Mirrors the server's formatPhoneForTwilio expectations (+1XXXXXXXXXX for NA,
// +61XXXXXXXXX for AU). AU is checked first since NA numbers never start with 0.
export const normalizePhone = (input: string): string | null => {
  const digits = (input || '').replace(/\D/g, '');

  // Australia: 0XXXXXXXXX (10), 61 + 9 digits (11), or 4XXXXXXXX mobile (9)
  if (digits.length === 10 && digits.startsWith('0')) return `+61${digits.slice(1)}`;
  if (digits.length === 11 && digits.startsWith('61')) return `+${digits}`;
  if (digits.length === 9 && digits.startsWith('4')) return `+61${digits}`;

  // North America: 10 digits, or 11 starting with 1
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;

  return null;
};

export const isEmail = (input: string): boolean => /^\S+@\S+\.\S+$/.test(input.trim());

interface RecipientFieldProps {
  recipients: ComposeRecipient[];
  onAdd: (recipient: ComposeRecipient) => void;
  onRemove: (index: number) => void;
  /** Phase 2 ships single-recipient; Phase 4 lifts this for group SMS. */
  maxRecipients?: number;
  disabled?: boolean;
  /** container styling override — lets the parent embed the field in a shared header */
  className?: string;
  /** opens the parent-owned "new customer" modal, pre-filled from the typed input */
  onRequestCreateContact?: (initial: CreateContactSeed) => void;
  /** opens the parent-owned editor for an already-added recipient */
  onEdit?: (index: number) => void;
  /** Phones: full-width results with taller rows; a tap on a chip edits it. */
  isMobile?: boolean;
}

export default function RecipientField({
  recipients,
  onAdd,
  onRemove,
  maxRecipients = 1,
  disabled = false,
  className,
  onRequestCreateContact,
  onEdit,
  isMobile = false,
}: RecipientFieldProps) {
  const auth = useSelector((state: any) => state.auth);
  const businessGroup = useSelector((state: any) => state.businessGroup);
  const maskPhone = usePhoneMask();
  const isEnterpriseEnabled = !!auth?.user?.isEnterpriseEnabled;
  const [input, setInput] = useState('');
  const [results, setResults] = useState<SearchedCustomer[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // select() blurs the input on the create-customer path; this flag lets the
  // blur handler skip its own auto-commit so we don't double-handle the input.
  const isSelectingRef = useRef(false);

  // Focus the field on mount. We do this explicitly (rather than the autoFocus
  // attribute) so it reliably wins over the composer's own focus handling.
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);

  // Email conversations are 1:1 only — an email-only recipient can't be in a
  // group, so once one is committed the field is full regardless of the cap.
  const hasEmailOnlyRecipient = recipients.some((r) => (r.phoneEmail || '').includes('@'));
  const atCapacity = recipients.length >= maxRecipients || hasEmailOnlyRecipient;

  const search = useMemo(
    () =>
      debounce(async (term: string) => {
        try {
          const res = await axios.post(
            `${getSearchUrl(window.location.href)}/api/customer-search`,
            {
              searchTerm: term,
              isEnterpriseRequested: isEnterpriseEnabled,
            },
            { withCredentials: true },
          );
          // most recently messaged first — the business is far more likely to
          // be reaching for a recent contact than an old one
          const sortedCustomers = (res.data?.matchingCustomers || [])
            .slice()
            .sort(
              (a: SearchedCustomer, b: SearchedCustomer) =>
                new Date(b.latestMessageDate || 0).getTime() -
                new Date(a.latestMessageDate || 0).getTime(),
            );
          setResults(sortedCustomers);
        } catch {
          setResults([]);
        } finally {
          setSearching(false);
        }
      }, 400),
    [isEnterpriseEnabled],
  );

  // Enterprise accounts can search across all locations, so each result needs
  // to show which location the customer belongs to — that's the location the
  // message will send from. Non-enterprise accounts only have one location.
  const getBusinessName = (_business?: string): string => {
    if (!_business || !Array.isArray(businessGroup)) return '';
    return businessGroup.find((b: any) => b?._id === _business)?.name || '';
  };

  const options: Option[] = useMemo(() => {
    const addedIds = new Set(recipients.map((r) => r._customer).filter(Boolean));

    const customerOptions: Option[] = results
      .filter((c) => !addedIds.has(c._id) && (c.phone || c.email))
      // groups are SMS-only — email-only customers can't be a 2nd+ recipient
      .filter((c) => recipients.length === 0 || c.phone)
      .map((customer) => ({ type: 'customer', customer }));

    const trimmed = input.trim();

    // Always offer a full create-customer flow (name optional, phone/email in a
    // modal) so the user can lead with a name, not just a phone/email. Keep it
    // pinned to the top so it's a predictable, always-available action.
    const lead: Option[] = trimmed ? [{ type: 'create-customer', query: trimmed }] : [];

    return [...lead, ...customerOptions];
  }, [results, input, recipients]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);
    setActiveIndex(0);

    if (value.trim().length >= MIN_SEARCH_CHARS) {
      setOpen(true);
      setSearching(true);
      setResults([]);
      search(value.trim());
    } else {
      setOpen(false);
      setResults([]);
    }
  };

  const reset = () => {
    setInput('');
    setResults([]);
    setOpen(false);
    setActiveIndex(0);
    inputRef.current?.focus();
  };

  const deriveInitialContact = (raw: string): CreateContactSeed => {
    const t = raw.trim();
    if (normalizePhone(t)) return { name: '', phone: t, email: '' };
    if (isEmail(t)) return { name: '', phone: '', email: t };
    return { name: t, phone: '', email: '' };
  };

  const select = (option: Option) => {
    isSelectingRef.current = true;
    if (option.type === 'customer') {
      const { customer } = option;
      onAdd({
        key: customer._id,
        _customer: customer._id,
        _business: customer._business,
        name: customer.name || customer.phone || customer.email || '',
        phoneEmail: customer.phone || customer.email || '',
        isNew: false,
      });
    } else {
      // create-customer — hand off to the parent-owned modal, pre-filled with
      // whatever was typed. Let the current click/focus sequence finish before
      // Radix mounts the dialog, otherwise the input and dialog fight for focus.
      const initialContact = deriveInitialContact(option.query);
      inputRef.current?.blur();
      setInput('');
      setResults([]);
      setOpen(false);
      setActiveIndex(0);
      window.requestAnimationFrame(() => {
        isSelectingRef.current = false;
        onRequestCreateContact?.(initialContact);
      });
      return;
    }
    reset();
    window.requestAnimationFrame(() => {
      isSelectingRef.current = false;
    });
  };

  // Find an already-known customer whose phone/email exactly matches the typed
  // value, so we can commit the real customer (with their name + _customer id)
  // rather than a brand-new contact.
  const findExactMatch = (raw: string): SearchedCustomer | undefined => {
    const phone = normalizePhone(raw);
    const email = isEmail(raw) ? raw.trim().toLowerCase() : null;
    if (!phone && !email) return undefined;
    return results.find(
      (c) =>
        (phone && c.phone && normalizePhone(c.phone) === phone) ||
        (email && (c.email || '').toLowerCase() === email),
    );
  };

  // Clear the typed value without re-focusing the input. Used by the
  // commit-on-blur flow, where the user is intentionally moving focus
  // elsewhere (e.g. into the message box) — reset() would steal it back.
  const clearInput = () => {
    setInput('');
    setResults([]);
    setActiveIndex(0);
  };

  // Turn a typed phone/email into a committed recipient chip (find-or-create).
  // Called when the user leaves the field so a visible-but-uncommitted value
  // doesn't silently fail at send time. Non-contact input (bare names, partial
  // numbers) is intentionally left untouched.
  const commitTypedInput = () => {
    const raw = input.trim();
    if (!raw || atCapacity) return;

    const phone = normalizePhone(raw);
    const email = isEmail(raw) ? raw.trim().toLowerCase() : null;
    if (!phone && !email) return;

    // groups are SMS-only — a typed email can only be the sole recipient
    if (email && !phone && recipients.length > 0) return;

    const value = phone || (email as string);

    // Already committed (e.g. typed a duplicate) — nothing to add.
    const alreadyAdded = recipients.some((r) => {
      const existing = normalizePhone(r.phoneEmail) || r.phoneEmail.toLowerCase();
      return existing === value;
    });
    if (alreadyAdded) {
      clearInput();
      return;
    }

    // Prefer a real customer match so downstream features (preview, invoices)
    // resolve. Otherwise commit a new contact named after the number/email.
    const match = findExactMatch(raw);
    if (match) {
      onAdd({
        key: match._id,
        _customer: match._id,
        _business: match._business,
        name: match.name || match.phone || match.email || '',
        phoneEmail: match.phone || match.email || '',
        isNew: false,
      });
    } else {
      onAdd({
        key: value,
        name: phone ? formatPhoneForTitleBar(phone) : (email as string),
        phoneEmail: value,
        isNew: true,
      });
    }
    clearInput();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && input === '' && recipients.length > 0) {
      onRemove(recipients.length - 1);
      return;
    }

    if (!open || options.length === 0) {
      if (e.key === 'Escape') setOpen(false);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + options.length) % options.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(options[Math.min(activeIndex, options.length - 1)]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const renderOption = (option: Option, index: number) => {
    const isActive = index === activeIndex;
    const baseClass = cn(
      'flex items-center gap-3 px-3 py-2 cursor-pointer text-sm',
      isMobile && 'min-h-12 px-4 text-[15px]',
      isActive && 'bg-accent text-accent-foreground',
    );

    const select_ = (e: React.MouseEvent) => {
      e.preventDefault(); // keep input focus
      select(option);
    };

    const keepInputFocused = (e: React.MouseEvent) => {
      e.preventDefault();
    };

    const selectAfterClick = (e: React.MouseEvent) => {
      e.preventDefault();
      select(option);
    };

    if (option.type === 'customer') {
      const { customer } = option;
      const locationName = isEnterpriseEnabled ? getBusinessName(customer._business) : '';
      const displayName = customer.name || customer.phone || customer.email || '';
      return (
        <div
          key={customer._id}
          className={baseClass}
          onMouseDown={select_}
          onMouseEnter={() => setActiveIndex(index)}
          role="option"
          tabIndex={-1}
          aria-selected={isActive}
        >
          <CustomerAvatar
            customer={customer}
            className="size-7 shrink-0"
            fallbackClassName="text-[11px]"
            colorSeed={displayName || customer._id}
          />
          <div className="flex flex-col min-w-0">
            <span className="font-medium truncate">{maskPhone(customer.name) || 'Unnamed'}</span>
            {locationName && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                <MapPin className="size-3 shrink-0" />
                {locationName}
              </span>
            )}
          </div>
          <span className="text-muted-foreground truncate ml-auto pl-2 shrink-0">
            {customer.phone ? maskPhone(formatPhoneForTitleBar(customer.phone)) : customer.email}
          </span>
        </div>
      );
    }

    // create-customer — pinned to the top, set apart from the search results below it
    const createClass = cn(
      baseClass,
      (searching || options.length > 1) && 'border-b border-border mb-1',
    );
    return (
      // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard selection is handled by the combobox input
      <div
        key={`create-${option.query}`}
        className={createClass}
        onMouseDown={keepInputFocused}
        onClick={selectAfterClick}
        onMouseEnter={() => setActiveIndex(index)}
        role="option"
        tabIndex={-1}
        aria-selected={isActive}
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted">
          <UserPlus className="size-4 text-muted-foreground" />
        </span>
        <span className="truncate">
          Create new customer: <span className="font-medium">{option.query}</span>
        </span>
      </div>
    );
  };

  const createOption = options.find((option) => option.type === 'create-customer');
  const customerOptions = options.filter((option) => option.type === 'customer');

  // While the field is untouched, add a gentle pulsing highlight so less
  // technical users notice where to start. It clears the moment they add a
  // recipient or begin typing.
  const showEmptyHint = recipients.length === 0 && input.trim() === '' && !disabled;
  const emptyPrompt = isMobile ? 'Name, phone or email' : 'Type a name, phone number, or email';

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: click only refocuses the inner input
    // biome-ignore lint/a11y/noStaticElementInteractions: click only refocuses the inner input
    <div
      className={cn(
        'relative flex items-center flex-wrap gap-1.5',
        className ?? 'border-b px-4 py-3',
      )}
      onClick={() => inputRef.current?.focus()}
    >
      <span className="text-base font-semibold text-foreground shrink-0">To:</span>

      {recipients.map((recipient, index) => {
        // A number the user just typed isn't hidden from them
        const label = recipient.isNew
          ? recipient.name || recipient.phoneEmail
          : maskPhone(recipient.name || recipient.phoneEmail);
        if (isMobile) {
          // Phones: the chip itself edits, and its X has a 44px hit area.
          return (
            <Badge
              key={recipient.key}
              variant="secondary"
              className="h-8 max-w-[calc(100%-2.5rem)] gap-0 rounded-full py-0 pl-0 pr-1 text-[15px] font-normal"
            >
              <button
                type="button"
                disabled={!onEdit}
                aria-label={onEdit ? `Edit ${label}` : undefined}
                className="min-w-0 truncate rounded-full py-1 pl-3 pr-1 text-left disabled:cursor-default"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(index);
                }}
              >
                {label}
              </button>
              <button
                type="button"
                aria-label={`Remove ${label}`}
                className="relative flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground before:absolute before:-inset-2.5 active:bg-muted-foreground/20"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(index);
                }}
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </Badge>
          );
        }
        return (
        <Badge
          key={recipient.key}
          variant="secondary"
          className="gap-1 py-1 px-2 text-sm font-normal max-w-[240px]"
        >
          <span className="truncate">{label}</span>
          {onEdit && (
            <button
              type="button"
              aria-label={`Edit ${label}`}
              className="rounded-sm opacity-60 hover:opacity-100 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(index);
              }}
            >
              <Pencil className="size-3" />
            </button>
          )}
          <button
            type="button"
            aria-label={`Remove ${label}`}
            className="rounded-sm opacity-60 hover:opacity-100 focus:outline-none"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
          >
            <X className="size-3" />
          </button>
        </Badge>
        );
      })}

      {!atCapacity && (
        <div className={cn('relative flex-1', isMobile ? 'min-w-[8rem]' : 'min-w-[200px]')}>
          <input
            ref={inputRef}
            disabled={disabled}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              // An option was just selected (which blurs the input on the
              // create path) — let select() own that flow.
              if (isSelectingRef.current) {
                setOpen(false);
                return;
              }
              commitTypedInput();
              setOpen(false);
            }}
            placeholder={recipients.length === 0 ? emptyPrompt : ''}
            className={cn(
              'w-full bg-transparent outline-none text-base font-medium py-2 placeholder:font-medium placeholder:text-muted-foreground',
              // hand the prompt off to the shimmering overlay while empty
              showEmptyHint && 'placeholder:text-transparent',
            )}
            role="combobox"
            aria-expanded={open}
            aria-controls="recipient-field-listbox"
            type="text"
            enterKeyHint={isMobile ? 'search' : undefined}
            aria-label="To"
            name="recipient-search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            data-bwignore
            data-form-type="other"
          />
          {showEmptyHint && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-0 flex items-center gap-2.5 text-base font-semibold"
            >
              <span className="animate-text bg-[length:200%_auto] bg-gradient-to-r from-primary/60 via-primary to-primary/60 bg-clip-text text-transparent">
                {emptyPrompt}
              </span>
            </span>
          )}
        </div>
      )}

      {open && (
        <div
          id="recipient-field-listbox"
          role="listbox"
          className={cn(
            'absolute left-4 right-4 top-full z-50 mt-1 max-w-md rounded-md border bg-popover text-popover-foreground shadow-md max-h-72 overflow-y-auto',
            // Phones: the results span the screen and stop above the keyboard.
            isMobile &&
              'left-0 right-0 mt-0 max-h-[calc(var(--app-height)*0.55)] max-w-none overscroll-contain rounded-none border-x-0 shadow-lg',
          )}
        >
          {createOption && renderOption(createOption, 0)}

          {searching ? (
            <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Searching matching customers…
            </div>
          ) : customerOptions.length === 0 ? (
            <div className="px-3 py-2.5 text-sm text-muted-foreground">
              No matching customers found
            </div>
          ) : (
            customerOptions.map((option, index) => renderOption(option, index + 1))
          )}
        </div>
      )}
    </div>
  );
}
